import { JwtPayload, jwtDecode } from 'jwt-decode';

import { createSpoofingNotAllowedError } from '../errors';
import SessionStore from '../legacy/SessionStore';
import UserService from '../legacy/UserService';
import { clearAuthProvider, rememberAuthProvider } from '../provider';
import { validateAdminRole } from '../roleValidation';
import type {
  AuthAccessTokenOptions,
  AuthAdapter,
  AuthImpersonationRequest,
  AuthLogoutOptions,
  AuthNotice,
  AuthStartLoginOptions,
  AuthState,
} from '../types';

const authNoticeStorageKey = 'authNotice';
const postLoginRedirectStorageKey = 'authV2PostLoginRedirect';
const sessionCookieGrantType = 'urn:ietf:params:oauth:grant-type:luxury-escapes-session-cookie';
const tokenFreshnessBufferMs = 60 * 1000;

type ExchangeAccessTokenOptions = {
  impersonation?: AuthImpersonationRequest;
  persistToken?: boolean;
  shouldLogoutOnFailure?: boolean;
};

export class AuthV2Adapter implements AuthAdapter {
  private initialized = false;

  private listeners = new Set<() => void>();

  private readonly sessionStore: SessionStore;

  private readonly userService: UserService;

  private sessionError: Error | null = null;

  private pendingAdminTokenExchange: Promise<string | null> | null = null;

  private pendingAdminTokenExchangeOptions: ExchangeAccessTokenOptions | null = null;

  private state: AuthState;

  constructor() {
    this.sessionStore = new SessionStore();
    this.userService = new UserService({ baseUri: window.configs.API_HOST }, false);
    this.state = this.computeState();

    this.sessionStore.addChangeListener(() => {
      this.notify();
    });
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    void this.resolveSession(false);
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): AuthState {
    return this.state;
  }

  static redirectToLogin(returnTo = window.location.href) {
    const { postLoginRedirectPath, redirectUri } = AuthV2Adapter.normalizeReturnTo(returnTo);

    rememberAuthProvider('v2');

    if (postLoginRedirectPath) {
      localStorage.setItem(postLoginRedirectStorageKey, postLoginRedirectPath);
    } else {
      localStorage.removeItem(postLoginRedirectStorageKey);
    }

    window.location.assign(
      AuthV2Adapter.getAuthServiceUrl('/api/auth/v2/external/sign-in', {
        provider: 'admin-portal',
        auth_origin: window.configs.AUTH_HOST,
        redirect_uri: redirectUri,
      }),
    );
  }

  async startLogin(options?: AuthStartLoginOptions): Promise<void> {
    AuthV2Adapter.redirectToLogin(options?.returnTo);
  }

  async completeLoginIfNeeded(): Promise<void> {
    return;
  }

  async getAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    return this.getV2AccessToken(false, options);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    return this.getV2AccessToken(true, options);
  }

  async logout(options?: AuthLogoutOptions): Promise<void> {
    if (options?.reason === 'session-expired') {
      localStorage.setItem(authNoticeStorageKey, options.reason);
    }

    // Order matters: localStorage, cookie, and adapter-state writes must all complete
    // before window.location.assign so they're flushed when the page unloads — the
    // assign call is the last thing we do. The session cookie itself is HttpOnly and
    // gets cleared by the auth service as part of the external sign-out redirect.
    this.resetSession();
    this.userService.delToken();
    clearAuthProvider();
    this.notify();
    window.location.assign(
      AuthV2Adapter.getAuthServiceUrl('/api/auth/v2/external/sign-out', {
        auth_origin: window.configs.AUTH_HOST,
        redirect_uri: window.location.origin,
      }),
    );
  }

  isAuthenticated() {
    return !!this.sessionStore.get()?.account;
  }

  async refreshSession(): Promise<void> {
    await this.resolveSession(true);
  }

  consumeNotice(): AuthNotice | null {
    const notice = localStorage.getItem(authNoticeStorageKey);
    localStorage.removeItem(authNoticeStorageKey);

    if (notice === 'session-expired') {
      return { type: 'session-expired' };
    }

    return null;
  }

  private async resolveSession(force = false): Promise<void> {
    if (!force && (this.sessionError || !this.sessionStore.empty())) {
      return;
    }

    this.sessionStore.set({
      loading: true,
      account: null,
    });

    const accessToken = (await this.getV2AccessToken(false, undefined, false)) ?? this.getStoredV2Token();

    if (!accessToken) {
      this.sessionError = null;
      this.sessionStore.set({
        loading: false,
        account: null,
      });
      this.notify();
      return;
    }

    await new Promise<void>((done) => {
      this.userService.me((error, account) => {
        const sessionError = error ?? validateAdminRole(account);

        if (sessionError) {
          this.sessionError = sessionError;
          this.userService.delToken();
          this.sessionStore.set({
            loading: false,
            account: null,
          });
        } else {
          this.sessionError = null;
          this.sessionStore.set({
            loading: false,
            account,
          });
          this.restorePostLoginRedirect();
        }

        done();
      });
    });
  }

  private async getV2AccessToken(
    forceRefresh: boolean,
    options?: AuthAccessTokenOptions,
    shouldLogoutOnFailure = true,
  ): Promise<string | null> {
    if (options?.impersonation) {
      return this.exchangeImpersonationAccessToken(options.impersonation);
    }

    const token = this.userService.getToken();

    if (!forceRefresh && token && this.isV2TokenFresh(token)) {
      return token;
    }

    return this.exchangeAdminAccessToken({ shouldLogoutOnFailure });
  }

  private isV2TokenFresh(token: string): boolean {
    if (!AuthV2Adapter.isV2Token(token)) {
      return false;
    }

    try {
      const { exp } = jwtDecode<JwtPayload>(token);

      if (!exp) {
        return false;
      }

      return exp * 1000 > Date.now() + tokenFreshnessBufferMs;
    } catch (_error) {
      return false;
    }
  }

  // Guards against a legacy token lingering in localStorage under the same key when reusing
  // a cached token for V2 access-token requests.
  private static isV2Token(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload & { ver?: string }>(token);
      return decoded.ver === '2.0';
    } catch (_error) {
      return false;
    }
  }

  private hasStoredV2Token() {
    return !!this.getStoredV2Token();
  }

  private getStoredV2Token() {
    const token = this.userService.getToken();

    return token && AuthV2Adapter.isV2Token(token) ? token : null;
  }

  private async exchangeAdminAccessToken(options?: Pick<ExchangeAccessTokenOptions, 'shouldLogoutOnFailure'>) {
    if (this.pendingAdminTokenExchange) {
      if ((options?.shouldLogoutOnFailure ?? true) && this.pendingAdminTokenExchangeOptions) {
        this.pendingAdminTokenExchangeOptions.shouldLogoutOnFailure = true;
      }
      return this.pendingAdminTokenExchange;
    }

    this.pendingAdminTokenExchangeOptions = {
      persistToken: true,
      shouldLogoutOnFailure: options?.shouldLogoutOnFailure,
    };

    this.pendingAdminTokenExchange = this.exchangeAccessToken(this.pendingAdminTokenExchangeOptions).finally(() => {
      this.pendingAdminTokenExchange = null;
      this.pendingAdminTokenExchangeOptions = null;
    });

    return this.pendingAdminTokenExchange;
  }

  private async exchangeImpersonationAccessToken(impersonation: AuthImpersonationRequest) {
    return this.exchangeAccessToken({
      impersonation,
      persistToken: false,
      shouldLogoutOnFailure: true,
    });
  }

  private async exchangeAccessToken(options: ExchangeAccessTokenOptions = {}): Promise<string | null> {
    const { impersonation, persistToken = true } = options;

    let response: Response;

    try {
      response = await fetch(AuthV2Adapter.getAuthServiceUrl('/api/auth/v2/token'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: this.toFormBody(impersonation),
      });
    } catch (_networkError) {
      // Transient network failure — leave session state alone so the caller can retry.
      return null;
    }

    const body = await response.json().catch(() => ({}));
    const accessToken = body?.access_token ?? body?.result?.access_token ?? null;

    if (response.ok && accessToken) {
      if (persistToken) {
        this.userService.setToken(accessToken);
        this.notify();
      }
      return accessToken;
    }

    // 403 on an impersonation exchange means the admin is not permitted to spoof this user.
    // Surface it as a targeted error and keep the admin session intact.
    if (impersonation && response.status === 403) {
      throw createSpoofingNotAllowedError();
    }

    // 401 is the only status that indicates a revoked session cookie. Every other
    // failure (403, 5xx, malformed body) is a request-level error and must not
    // destroy the existing admin session.
    if ((options.shouldLogoutOnFailure ?? true) && response.status === 401 && this.hasStoredV2Token()) {
      await this.logout({ reason: 'session-expired' });
      return null;
    }

    if (persistToken && response.status === 401) {
      this.userService.delToken();
      this.notify();
    }

    return null;
  }

  private toFormBody(impersonation?: AuthImpersonationRequest) {
    const searchParams = new URLSearchParams();
    searchParams.set('grant_type', sessionCookieGrantType);

    if (impersonation?.customerId) {
      searchParams.set('subject_to_impersonate_id', impersonation.customerId);
    }

    return searchParams.toString();
  }

  private static getAuthServiceUrl(path: string, query?: Record<string, string>) {
    const url = new URL(path, window.configs.AUTH_HOST);

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    return url.toString();
  }

  private static normalizeReturnTo(returnTo: string) {
    // The auth service is responsible for allowlisting redirect origins, but we defensively
    // clamp redirect_uri to the current origin so a caller can never steer the redirect off-site.
    try {
      const returnToUrl = new URL(returnTo, window.location.origin);
      const postLoginRedirectPath =
        returnToUrl.origin === window.location.origin
          ? `${returnToUrl.pathname}${returnToUrl.search}${returnToUrl.hash}`
          : null;

      return {
        redirectUri: window.location.origin,
        postLoginRedirectPath: postLoginRedirectPath && postLoginRedirectPath !== '/' ? postLoginRedirectPath : null,
      };
    } catch (_error) {
      return {
        redirectUri: window.location.origin,
        postLoginRedirectPath: null,
      };
    }
  }

  private restorePostLoginRedirect() {
    const redirectPath = localStorage.getItem(postLoginRedirectStorageKey);

    if (!redirectPath) {
      return;
    }

    localStorage.removeItem(postLoginRedirectStorageKey);

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (currentPath !== '/' || redirectPath === currentPath || !redirectPath.startsWith('/')) {
      return;
    }

    window.location.replace(redirectPath);
  }

  private resetSession() {
    this.sessionError = null;
    this.sessionStore.set({
      loading: false,
      account: null,
    });
  }

  private notify() {
    this.state = this.computeState();
    this.listeners.forEach((listener) => listener());
  }

  private computeState(): AuthState {
    const session = this.sessionStore.get();
    const token = this.userService.getToken();

    return {
      loading: session?.loading ?? true,
      authenticated: !!session?.account,
      token,
      user: session?.account ?? null,
    };
  }
}
